import { useEffect, useState } from 'react';

import jwtDecode from 'jwt-decode';

import { useCustomAuth } from 'auth/AuthContext';
import useConfig from 'hooks/useConfig';
import { AuthConstants } from 'models/enums';

export type LDUser = {
    key: string;
    email: string;
    custom: {
        role: string;
        curedTenantId: string;
    };
};

/**
 * Gets the LD user used for targeting.
 * @returns The LD user or undefined if the user is not authenticated
 */
const useLDUser = (): LDUser | undefined => {
    const { isAuthenticated, user, isInnovaccerLogin, getAccessTokenSilently } = useCustomAuth();
    const [ldUser, setLdUser] = useState<LDUser | undefined>(undefined);

    const {
        config: { AUTH0_AUDIENCE_ENDPOINT },
    } = useConfig();

    useEffect(() => {
        const fetchAdditionalUserData = async () => {
            if (!isAuthenticated) return;

            if (isInnovaccerLogin) {
                const { email, role } = user;
                const ldUser: LDUser = {
                    key: email,
                    email,
                    custom: { role, curedTenantId: user[AuthConstants.CURED_TENANT_ID] },
                };
                setLdUser(ldUser);
            } else {
                let curedTenantId: string;
                const { email } = user;
                const roles = user[AUTH0_AUDIENCE_ENDPOINT];
                const role: string = roles[0];
                try {
                    const token = await getAccessTokenSilently();
                    const decodedToken = jwtDecode(token as string);
                    curedTenantId = (decodedToken as any).permissions[0].split(':')[1];
                } catch (error) {
                    curedTenantId = '';
                }
                const ldUser: LDUser = { key: email, email, custom: { role, curedTenantId } };
                setLdUser(ldUser);
            }
        };
        fetchAdditionalUserData();
    }, [isAuthenticated, user, isInnovaccerLogin, AUTH0_AUDIENCE_ENDPOINT, getAccessTokenSilently]);

    if (!isAuthenticated) return;

    return ldUser;
};

export default useLDUser;
